import { useEffect, useRef, useState } from "react";
import type { GameUploadFormat } from "@/domain/types";
import type { ImportPreview } from "@/hardware/import-pipeline/preview";
import type { ImportTarget } from "@/hardware/import-pipeline/use-import-pipeline";

interface ImportModalProps {
  open: boolean;
  /** Which upload opened the modal (Profile vs Keyboard); scopes the accept filter and copy.
   *  null for a generic/global open. */
  target: ImportTarget | null;
  /** The active Game's hotkey-upload format. When the Profile upload opens the modal, its copy and
   *  browse filter name just this Game's format (.SC2Hotkeys vs cvars.xml). Null falls back to the
   *  game-neutral profile copy (a global drop/paste, or a Game that declares no format). */
  profileFormat?: GameUploadFormat | null;
  /** Parsed candidate awaiting Apply; null shows the idle drop-or-paste state. */
  preview: ImportPreview | null;
  /** Parse failure to show inline in the idle state. */
  error: string | null;
  /** Half of a two-file board pair was received; names the file that completes it. */
  awaiting: string | null;
  onText: (text: string, fileName?: string) => void;
  onFiles: (files: File[]) => void;
  onApply: () => void;
  onClose: () => void;
}

/** Every format the document-level drop/paste accepts; the DropOverlay subline and the generic
 *  modal copy share this one enumeration so they cannot drift. */
export const GENERIC_FORMATS = "KLE raw data, KLE / VIA / QMK .json, .SC2Hotkeys, cvars.xml";

/** Per-target modal chrome: title, file-input accept filter, drop-zone subline, paste
 *  placeholder. A null target (global drop/paste) uses the generic set. The `profile` entry is the
 *  game-neutral fallback naming both formats; when a Game's `profileFormat` is supplied, the Profile
 *  target instead names just that Game's format (composed below). Detection routes a dropped file to
 *  the right per-Game ingest regardless, so the copy scopes the advert, not the capability. */
const IMPORT_UI = {
  keyboard: {
    title: "Import keyboard",
    accept: ".json,.layout,application/json",
    sub: "KLE raw data, KLE / VIA / QMK .json, or click to browse",
    paste: "Paste KLE raw data or JSON…",
  },
  profile: {
    title: "Import hotkey profile",
    accept: ".SC2Hotkeys,.xml,.txt,text/plain",
    sub: ".SC2Hotkeys file or Destiny cvars.xml, or click to browse",
    paste: "Paste .SC2Hotkeys or cvars.xml text…",
  },
  generic: {
    title: "Import",
    accept: ".json,.txt,.layout,.SC2Hotkeys,.xml,application/json,text/plain",
    sub: `${GENERIC_FORMATS}, or click to browse`,
    paste: "Paste KLE raw data, JSON, .SC2Hotkeys, or cvars.xml text…",
  },
};

export function ImportModal({
  open,
  target,
  profileFormat,
  preview,
  error,
  awaiting,
  onText,
  onFiles,
  onApply,
  onClose,
}: ImportModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(false);
  const [paste, setPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");

  // close on Escape or on a press outside the dialog (the scrim itself stays non-interactive);
  // presses on the toast stack are not "outside" - dismissing a toast must not discard a preview
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e: MouseEvent) => {
      const el = modalRef.current;
      if (!el || !(e.target instanceof Element)) return;
      if (el.contains(e.target) || e.target.closest(".kl-toasts")) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onClose]);

  // a closed modal keeps its mounted state; clear the idle-state leftovers for the next open
  useEffect(() => {
    if (open) return;
    setPaste(false);
    setPasteText("");
    setOver(false);
  }, [open]);

  // role=dialog focus discipline: take focus on open, hold Tab inside (the scrim covers the page,
  // so walking the background would operate invisible controls), hand focus back to the opener on
  // close. Focusables are queried per keypress - the idle/preview states swap the dialog's body.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modalRef.current?.focus();
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const el = modalRef.current;
      if (!el) return;
      const focusables = [
        ...el.querySelectorAll<HTMLElement>("button, [href], input, textarea, select"),
      ].filter((f) => !f.hasAttribute("hidden") && !(f as HTMLButtonElement).disabled);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onTab);
    return () => {
      document.removeEventListener("keydown", onTab);
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  // a Game-scoped Profile upload names just that Game's format; everything else (Keyboard, a global
  // drop/paste, or a Game with no declared format) uses the static per-target copy.
  const ui =
    target === "profile" && profileFormat
      ? {
          title: IMPORT_UI.profile.title,
          accept: profileFormat.accept,
          sub: `${profileFormat.fileLabel}, or click to browse`,
          paste: `Paste ${profileFormat.pasteLabel} text…`,
        }
      : IMPORT_UI[target ?? "generic"];

  const readFiles = (files: FileList | null) => {
    const list = Array.from(files ?? []);
    if (list.length) onFiles(list);
  };

  return (
    <div className="kl-modal-scrim">
      <div
        ref={modalRef}
        className="kl-modal"
        role="dialog"
        aria-modal="true"
        aria-label={ui.title}
        tabIndex={-1}
      >
        <header className="kl-modal-hd">
          <div className="kl-modal-title">
            <b>{ui.title}</b>
          </div>
          <button type="button" className="kl-modal-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="kl-modal-bd">
          {preview ? (
            <>
              <div className="kl-file-row">
                <span className="kl-file-ok">✓</span>
                <div className="kl-file-meta">
                  <div className="kl-file-name">{preview.fileName ?? "Pasted text"}</div>
                  <div className="kl-file-sub">{preview.heading}</div>
                </div>
              </div>
              <div className="kl-stat-row">
                {preview.stats.map((s) => (
                  <div key={s.label} className={`kl-stat${s.tone ? ` ${s.tone}` : ""}`}>
                    <span className="kl-stat-n">{s.n}</span>
                    <span className="kl-stat-l">{s.label}</span>
                  </div>
                ))}
              </div>
              {preview.coverage ? (
                <div className="kl-cov">
                  <div className="kl-cov-head">
                    <span>On {preview.coverage.label}</span>
                    <span>
                      <b>{preview.coverage.bound}</b> / {preview.coverage.total} keys bound
                    </span>
                  </div>
                  <div className="kl-cov-bar">
                    <span
                      style={{
                        width: `${Math.round(
                          (preview.coverage.bound / Math.max(1, preview.coverage.total)) * 100,
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              ) : null}
              <div className="kl-modal-note">
                <span className="kl-note-dot" />
                {preview.note}
              </div>
              <div className="kl-modal-actions">
                <button type="button" className="kl-btn ghost" onClick={onClose}>
                  Cancel
                </button>
                <button type="button" className="kl-btn primary" onClick={onApply}>
                  Apply
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`kl-drop${over ? " is-over" : ""}${error ? " is-err" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  // preventDefault marks the drop as taken; the document-level listener in
                  // useImportPipeline still fires (resetting the drag overlay) but skips
                  // defaultPrevented drops, so the files import exactly once
                  e.preventDefault();
                  setOver(false);
                  readFiles(e.dataTransfer.files);
                }}
                onClick={() => inputRef.current?.click()}
              >
                <span className="kl-drop-big">{over ? "Drop to load" : "Drag a file here"}</span>
                <span className="kl-drop-sub">{ui.sub}</span>
              </button>
              {awaiting ? (
                <div className="kl-drop-part">
                  <span>+</span>
                  {awaiting}
                </div>
              ) : null}
              {error ? (
                <div className="kl-drop-error">
                  <span>!</span>
                  {error}
                </div>
              ) : null}
              {paste ? (
                <div className="kl-paste">
                  <textarea
                    className="kl-paste-ta"
                    aria-label="Paste import text"
                    value={pasteText}
                    spellCheck={false}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={ui.paste}
                  />
                  <div className="kl-paste-actions">
                    <button
                      type="button"
                      className="kl-link"
                      onClick={() => {
                        setPaste(false);
                        setPasteText("");
                      }}
                    >
                      cancel
                    </button>
                    <button
                      type="button"
                      className="kl-btn primary"
                      disabled={!pasteText.trim()}
                      onClick={() => onText(pasteText)}
                    >
                      Parse pasted text
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="kl-paste-link" onClick={() => setPaste(true)}>
                  or paste raw text…
                </button>
              )}
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ui.accept}
                hidden
                onChange={(e) => {
                  readFiles(e.currentTarget.files);
                  e.currentTarget.value = "";
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
